import React, { useEffect, useState, useRef } from 'react'

import {
    Box,
    Spinner,

    /* Hooks */
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { isHydrated } from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/* Styles */
import { MyRewardsStylesProvider } from './styles/myRewardsStyles'

/* Hooks */
import { useLoyalty } from '../../integrations/loyalty/use-loyalty'
import { useCurrentCustomer } from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useSafariDetection from '../../hooks/use-safari-detection'
import { useOptimizely } from '../../contexts/optimizely-context'

/* Providers */
import { OptimizelyProvider } from '../../contexts/optimizely-context';

/* Components */
import Banner from './partials/banner'
import AccountBlock from './partials/account-block'
import ExclusiveRewardsBlock from './partials/exclusive-rewards-block'
import ExclusiveRewardsBlockCMS from './partials/exclusive-rewards-block-cms'
import LoyaltyProgram from './partials/loyalty-program'
import EarningPoints from './partials/earning-points'
import FAQ from './partials/faq'
import HowItWorks from './partials/how-it-works'
import TilesAndApp from './partials/tiles-and-app'
import {useIntl} from 'react-intl'
import Seo from '../../components/seo'

const MyRewardsContent = () => {
    const {app: appConfig} = getConfig()
    const { rewardPageBlock, isOptimizelyLoading } = useOptimizely()
    const isSafari = useSafariDetection()
    const styles = useMultiStyleConfig('MyRewards', {isSafari: isSafari})
    const { syncLoyaltyData, isLoading: isLoyaltyDataLoading } = useLoyalty()
    const { data: customer, isLoading: isLoadingCustomer } = useCurrentCustomer()
    const [loyaltyData, setLoyaltyData] = useState(null)
    const exclusiveRewardsRef = useRef(null)
    const loyaltyProgramRef = useRef(null)
    const earningPointsRef = useRef(null)
    const [mounted, setMounted] = useState(false)
    const sflmEnabled = appConfig?.SFLM_ENABLED
    const {formatMessage} = useIntl()

    const rewardsPageTitle = formatMessage({
        id: 'rewards.page.title',
        defaultMessage: 'My Rewards | Garage Clothing UK'
    })

    const rewardsPageDescription = formatMessage({
        id: 'rewards.page.description',
        defaultMessage: 'Track your Rewards and Loyalty Program at Garage Clothing UK.'
    })

    useEffect(() => {
        setMounted(true)
    }, [])

    const fetchLoyaltyData = async () => {
        setLoyaltyData(await syncLoyaltyData())
    }

    useEffect(() => {
        if (sflmEnabled && customer?.isRegistered) {
            fetchLoyaltyData()
        }
    }, [customer?.isRegistered])

    const seo = <Seo title={rewardsPageTitle}
        description={rewardsPageDescription}
        openGraphTags={[{
                property: 'og:title',
                content: rewardsPageTitle
            } , {
                property: 'og:description',
                content: rewardsPageDescription
            }]}
        />

    if (!mounted) {
        return seo
    }

    if (isOptimizelyLoading && isLoyaltyDataLoading) {
        return (
            <>
                {seo}
                <Box data-testid="error-page">
                    <Spinner size="lg"
                        position={'absolute'}
                        left={'50%'}
                        top={'50%'}
                        transform={'translate(-50%, -50%)'}
                    />
                </Box>
            </>
        )
    }

    return (
        <>
            {seo}
            {isHydrated() &&
                <Box {...styles.container} data-testid="sf-my-rewards-container">
                    {/* Banner */}
                    <Banner customer={customer} isLoadingCustomer={isLoadingCustomer} />
                    {sflmEnabled &&
                        (customer.isRegistered ? (
                            <>
                                {/* Account block */}
                                <AccountBlock
                                    loyaltyData={loyaltyData}
                                    isLoyaltyDataLoading={isLoyaltyDataLoading}
                                    exclusiveRewardsRef={exclusiveRewardsRef}
                                    loyaltyProgramRef={loyaltyProgramRef}
                                    earningPointsRef={earningPointsRef} />

                                {/* Exclusive rewards block */}
                                {loyaltyData?.rewardsDataMapping ? (
                                    /* SFCC Content */
                                    <ExclusiveRewardsBlock
                                        ref={exclusiveRewardsRef}
                                        loyaltyData={loyaltyData}
                                        isLoyaltyDataLoading={isLoyaltyDataLoading}
                                    />
                                ) : (
                                    /* CMS Content only if we have configured */
                                    rewardPageBlock &&
                                        <ExclusiveRewardsBlockCMS
                                            ref={exclusiveRewardsRef}
                                            loyaltyData={loyaltyData}
                                            isLoyaltyDataLoading={isLoyaltyDataLoading}
                                        />
                                )}
                            </>) : (
                                /* How it works */
                                <HowItWorks />
                            ))
                    }

                    {/* Loyalty program */}
                    <LoyaltyProgram ref={loyaltyProgramRef} />

                    {/* Earning Points */}
                    <EarningPoints ref={earningPointsRef} customer={customer} isLoadingCustomer={isLoadingCustomer} />

                    {/* Tiles and app */}
                    {!customer.isRegistered &&
                        <TilesAndApp />
                    }

                    {/* FAQ */}
                    <FAQ />
                </Box>
            }
        </>
    )
}

const MyRewards = () => {
    const { data: customer } = useCurrentCustomer()
    const isSafari = useSafariDetection()
    const styles = useMultiStyleConfig('MyRewards', {isSafari: isSafari})
    const resolvePageIdentifier = () => customer?.isRegistered ? 'grg-web-uk-rewards-page-logged-in' : 'loyalty'
    const [pageIdentifier, setPageIdentifier] = useState(resolvePageIdentifier)

    useEffect(() => {
        setPageIdentifier(resolvePageIdentifier())
    }, [customer?.isRegistered])

    return (
        pageIdentifier &&
         <OptimizelyProvider pageIdentifier={pageIdentifier}>
            <MyRewardsStylesProvider value={styles}>
                <MyRewardsContent />
            </MyRewardsStylesProvider>
         </OptimizelyProvider>
    )
}

MyRewards.getTemplateName = () => 'my-rewards'

export default MyRewards
